import type { CacheEntry, CacheConfig, CacheManager as ICacheManager } from '../../types/location';

/**
 * In-memory cache with TTL support for location data
 * 
 * Features:
 * - TTL-based expiration
 * - Automatic cleanup of expired entries
 * - Memory limit enforcement
 * - Hit/miss rate tracking
 * - Pattern-based invalidation
 */
export class LocationCache implements ICacheManager {
  private readonly cache: Map<string, CacheEntry<unknown>>;
  private readonly config: CacheConfig;
  private stats: {
    hits: number;
    misses: number;
    sets: number;
    invalidations: number;
  };
  private cleanupInterval: NodeJS.Timeout | null;

  constructor(config: Partial<CacheConfig> = {}) {
    this.cache = new Map();
    this.config = {
      defaultTTL: config.defaultTTL || 3600000, // 1 hour default
      maxSize: config.maxSize || 1000 // Max 1000 entries
    };
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      invalidations: 0
    };

    // Start automatic cleanup every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.clearExpired();
    }, 300000); // 5 minutes
  }

  /**
   * Get value from cache
   * Returns null if key doesn't exist or entry is expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }

    this.stats.hits++;
    return entry.data as T;
  }

  /**
   * Set value in cache with optional TTL
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Enforce max size
    if (this.cache.size >= this.config.maxSize && !this.cache.has(key)) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    const effectiveTTL = ttl || this.config.defaultTTL;
    const now = Date.now();

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      ttl: effectiveTTL,
      expiresAt: now + effectiveTTL
    };

    this.cache.set(key, entry);
    this.stats.sets++;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Invalidate (remove) a specific key
   */
  invalidate(key: string): void {
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.stats.invalidations++;
    }
  }

  /**
   * Invalidate all keys matching a pattern
   */
  invalidatePattern(pattern: RegExp): void {
    let count = 0;
    
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key);
        count++;
      }
    }

    this.stats.invalidations += count;
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    this.stats.invalidations += size;
  }

  /**
   * Get entry data bypassing TTL check (for stale-data fallback)
   */
  peek<T>(key: string): T | null {
    const entry = this.cache.get(key);
    return entry ? (entry.data as T) : null;
  }

  /**
   * Remove expired entries
   */
  clearExpired(): number {
    const now = Date.now();
    let count = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        count++;
      }
    }

    if (count > 0) {
      this.stats.invalidations += count;
    }

    return count;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? (this.stats.hits / total) * 100 : 0;

    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      hits: this.stats.hits,
      misses: this.stats.misses,
      sets: this.stats.sets,
      invalidations: this.stats.invalidations,
      hitRate: hitRate.toFixed(2) + '%'
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      invalidations: 0
    };
  }

  /**
   * Get all cache keys
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache size
   */
  getSize(): number {
    return this.cache.size;
  }

  /**
   * Destroy cache and cleanup
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
  }
}

/**
 * Cache key generators for consistent key naming
 */
export const CacheKeys = {
  countries: () => 'countries:all',
  countryDetails: (iso2: string) => `country:${iso2}`,
  states: (countryIso2: string) => `states:${countryIso2}`,
  stateDetails: (countryIso2: string, stateIso2: string) => `state:${countryIso2}:${stateIso2}`,
  cities: (countryIso2: string, stateIso2: string) => `cities:${countryIso2}:${stateIso2}`,
  citiesByCountry: (countryIso2: string) => `cities:${countryIso2}:all`
};

/**
 * Recommended TTL values (in milliseconds)
 * Based on API documentation and data stability
 */
export const RecommendedTTL = {
  countries: 48 * 60 * 60 * 1000, // 48 hours - very stable data
  states: 24 * 60 * 60 * 1000, // 24 hours - occasional updates
  cities: 12 * 60 * 60 * 1000 // 12 hours - more frequent updates
};

/**
 * Create a singleton instance of LocationCache
 */
let cacheInstance: LocationCache | null = null;

export function getCacheManager(config?: Partial<CacheConfig>): LocationCache {
  cacheInstance ??= new LocationCache(config);
  return cacheInstance;
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetCacheManager(): void {
  if (cacheInstance) {
    cacheInstance.destroy();
    cacheInstance = null;
  }
}
